// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  getFirestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Environment configuration
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
const isDemo = process.env.REACT_APP_ENVIRONMENT === 'demo';
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';

// Force production config for localhost testing
const forceProduction = process.env.REACT_APP_FORCE_PRODUCTION === 'true';

// Development Firebase Config (NEW Multi-Tenant Database)
const devConfig = {
  apiKey: "AIzaSyA-NllyIEBgUZZXz2vY34doGxevI0Quzlo",
  authDomain: "bakery-multitenant-dev.firebaseapp.com",
  projectId: "bakery-multitenant-dev",
  storageBucket: "bakery-multitenant-dev.firebasestorage.app",
  messagingSenderId: "21920396425",
  appId: "1:21920396425:web:3bd7027f231001f431c258"
};

// Production Firebase Config (NEW Multi-Tenant Database)
const prodConfig = {
  apiKey: "AIzaSyA75O1kMxjgveaG6taQ9SghtL5CwFRq31A",
  authDomain: "bakery-multitenant-prod.firebaseapp.com",
  projectId: "bakery-multitenant-prod",
  storageBucket: "bakery-multitenant-prod.firebasestorage.app",
  messagingSenderId: "46653692705",
  appId: "1:46653692705:web:a16ed33888e01024eafc60"
};

// Demo Firebase Config (Separate database for tutorial/demo)
// NOTE: Replace with actual demo project credentials when created
const demoConfig = {
  apiKey: "REPLACE_WITH_DEMO_API_KEY",
  authDomain: "bakery-multitenant-demo.firebaseapp.com",
  projectId: "bakery-multitenant-demo",
  storageBucket: "bakery-multitenant-demo.firebasestorage.app",
  messagingSenderId: "REPLACE_WITH_DEMO_SENDER_ID",
  appId: "REPLACE_WITH_DEMO_APP_ID"
};

// Select config based on environment
const firebaseConfig = isDemo
  ? demoConfig
  : (isDevelopment && !forceProduction)
    ? devConfig
    : prodConfig;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);

// Initialize Firestore with IndexedDB persistence (Fix #5: try/catch for Safari private mode)
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
} catch (e) {
  console.warn('[firebase] IndexedDB persistence unavailable, falling back to memory cache:', e);
  db = getFirestore(app);
}
export { db };
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1'); // Specify region where functions are deployed

// Track emulator connection status
let emulatorsConnected = false;

// Connect to emulators in development (with proper error handling)
if (useEmulator && !emulatorsConnected) {
  
  try {
    // Connect Auth emulator
    if (!auth._delegate?._config?.emulator) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    }
  } catch (error) {
  }

  try {
    // Connect Firestore emulator
    if (!db._delegate?._databaseId?.database?.includes('firestore-emulator')) {
      connectFirestoreEmulator(db, 'localhost', 8080);
    }
  } catch (error) {
  }

  try {
    // Connect Storage emulator
    if (!storage._delegate?._host?.includes('localhost')) {
      connectStorageEmulator(storage, 'localhost', 9199);
    }
  } catch (error) {
  }

  try {
    // Connect Functions emulator
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch (error) {
  }

  emulatorsConnected = true;
}

// Export current environment info
export const firebaseEnv = {
  isDevelopment: isDevelopment && !forceProduction,
  isDemo,
  useEmulator,
  forceProduction,
  projectId: firebaseConfig.projectId,
  environment: isDemo ? 'demo' : (isDevelopment && !forceProduction) ? 'development' : 'production'
};

// Log current configuration
if (useEmulator) {
}